import React, {useCallback, useEffect, useRef} from "react";
import {Link as RouterLink, useLocation, useNavigate, useParams} from "react-router";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {DocumentTypeProvider} from "../document-type/documentTypeContext";
import SearchResultDetails from "../search-result-details/SearchResultDetails";
import {
    SearchResultDetailsProvider,
    useSearchResultDetailsTranslation,
} from "../search-result-details/SearchResultDetailsContext";
import {useDocumentByUniqueId} from "./useDocumentByUniqueId";
import {usePageTitle} from "./usePageTitle";
import {useStandaloneDetails} from "./useStandaloneDetails";

// Stryker disable ObjectLiteral,StringLiteral: layout only, nothing a test could observe
const STYLES = {
    page: {p: 3},
    loading: {display: 'flex', alignItems: 'center', gap: 2},
};
// Stryker restore ObjectLiteral,StringLiteral

/**
 * /documents/:uniqueId (DE-1719): opens one document in the existing details view. A document that does not
 * exist or that the user may not read is "not found" alike. Closing goes back, or to the search when the link
 * was opened directly.
 */
export const DocumentRoute = () => <SearchResultDetailsProvider>
    <DocumentTypeProvider>
        <DocumentRouteContent/>
    </DocumentTypeProvider>
</SearchResultDetailsProvider>;

const DocumentRouteContent = () => {
    const t = useSearchResultDetailsTranslation();
    const {uniqueId} = useParams();
    const {loading, document, notFound} = useDocumentByUniqueId(uniqueId);
    const navigate = useNavigate();
    const location = useLocation();
    const loadingTitle = t("documentLoading", "Laster dokumentet");
    const notFoundTitle = t("documentNotFoundTitle", "Dokument ikke funnet");
    usePageTitle(loading ? loadingTitle : notFound ? notFoundTitle : document?.title || t("documentTitle", "Dokument"));

    // React Router keys the first location of a tab "default": no DAMS page to go back to.
    const goBack = useCallback(() => navigate(location.key === "default" ? "/search" : -1),
        [navigate, location.key]);
    useStandaloneDetails(document, goBack);

    if (loading) {
        return <Box sx={STYLES.page}>
            <Box sx={STYLES.loading}>
                <CircularProgress aria-label={loadingTitle}/>
                <FocusedHeading>{loadingTitle}</FocusedHeading>
            </Box>
        </Box>;
    }
    if (notFound) {
        return <Box sx={STYLES.page}>
            <FocusedHeading>{notFoundTitle}</FocusedHeading>
            <Typography role="status">
                {t("documentNotFound", "Fant ikke dokumentet, eller du har ikke tilgang til det.")}
            </Typography>
            <Link component={RouterLink} to="/search">{t("documentBackToSearch", "Til søket")}</Link>
        </Box>;
    }
    return <SearchResultDetails/>;
};

// DE-1719 (UX D1, D2): the state's heading takes focus on arrival, so screen readers announce where they are.
const FocusedHeading = ({children}) => {
    const heading = useRef(null);
    useEffect(() => heading.current?.focus(), []);
    return <Typography variant="h5" component="h1" ref={heading} tabIndex={-1}>{children}</Typography>;
};
